import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CallStatus } from '../../enums';
import { DailyLossReachedEvent, MaxLossReachedEvent, ProfitTargetReachedEvent, SSE_EVENT_TYPES, SSEEvent } from '../../services/closeout/sse-event-types';
import { AccountCloseoutResponse } from '../../services/closeout/types';
import { ErrorPayloadFull } from '../common-actions';

import { INITIAL_STATE } from './constants';
import { setCloseoutCallStatus } from './helpers';
import { CloseoutCall } from './types';

const closeoutSlice = createSlice({
  name: 'closeout',
  initialState: INITIAL_STATE,
  reducers: {
    getAccountCloseoutStatus(state, _action: PayloadAction<string>) {
      state.accountStatusData = INITIAL_STATE.accountStatusData;
      setCloseoutCallStatus(CloseoutCall.getAccountCloseoutStatus, CallStatus.PENDING, state);
    },
    fetchCloseoutStatusOnLastClose(_state, _action: PayloadAction<string>) {},
    resetAccountCloseoutState(state) {
      state.accountStatusData = INITIAL_STATE.accountStatusData;
      state.sseConnected = INITIAL_STATE.sseConnected;
      state.sseError = INITIAL_STATE.sseError;
      state.profitTargetReached = INITIAL_STATE.profitTargetReached;
      state.isTradingPlanCompleted = INITIAL_STATE.isTradingPlanCompleted;
      state.isDailyAutoClose = INITIAL_STATE.isDailyAutoClose;
    },
    getAccountCloseoutStatusCompleted(state, action: PayloadAction<AccountCloseoutResponse>) {
      const isDailyResetProcessing = action.payload.dailyResetProcessing;
      const isMaxLossReached = action.payload.totalLossReached;
      if (isDailyResetProcessing) {
        state.accountStatusData = { ...INITIAL_STATE.accountStatusData, dailyResetProcessing: true };
      } else if (isMaxLossReached) {
        state.accountStatusData = { ...action.payload, dailyLossReached: false };
      } else {
        state.accountStatusData = action.payload;
      }
      setCloseoutCallStatus(CloseoutCall.getAccountCloseoutStatus, CallStatus.READY, state);
    },
    getAccountCloseoutStatusFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setCloseoutCallStatus(CloseoutCall.getAccountCloseoutStatus, CallStatus.ERROR, state, status);
    },

    // SSE Connection actions
    sseConnectionClosed(state) {
      state.sseConnected = false;
    },
    sseConnectionError(state, action: PayloadAction<string>) {
      state.sseConnected = false;
      state.sseError = action.payload;
    },

    // SSE Event received
    sseEventReceived(state, action: PayloadAction<SSEEvent>) {
      const event = action.payload;

      // Handle different event types
      switch (event.type) {
        case SSE_EVENT_TYPES.ACCOUNT_STATUS_UPDATE:
          // Extract tradeEvent data from event.data
          if ('data' in event && event.data && typeof event.data === 'object') {
            const eventData = event.data as {
              tradeEvent?: {
                dailyLoss: number;
                maxLoss: number;
                planTargetsReached: boolean;
                profitTargetReached: boolean;
                unrealizedPnl: number;
                realizedPnl: number;
              };
              dailyResetEvent?: {
                isCompleted: boolean;
                rewindCount: number;
              } | null;
              tradeCloseEvent?: {
                dailyLoss: number;
                maxLoss: number;
                realizedPnl: number;
                unrealizedPnl: number;
                tradingDays: number;
                consistencyCoeff: number;
                consistencyScore: number;
              };
            };
            const tradeEvent = eventData?.tradeEvent;
            const dailyResetEvent = eventData?.dailyResetEvent;
            const tradeCloseEvent = eventData?.tradeCloseEvent;

            if (tradeEvent && 'tradeEvent' in event.data) {
              // Update accountStatusData with real-time values
              state.accountStatusData.tradeEvent = eventData.tradeEvent;
              state.accountStatusData.dailyLoss = tradeEvent.dailyLoss;
              state.accountStatusData.maxLoss = tradeEvent.maxLoss;
              state.accountStatusData.totalUnrealizedPnl = tradeEvent.unrealizedPnl;
              // Update profit target flag for modal display
              if (tradeEvent.profitTargetReached && !state.profitTargetReached) {
                state.profitTargetReached = true;
              }
            }

            // Update dailyResetEvent if present in SSE payload
            if (dailyResetEvent && 'dailyResetEvent' in eventData) {
              state.accountStatusData.dailyResetEvent = eventData.dailyResetEvent;
              state.accountStatusData.dailyResetProcessing = !dailyResetEvent.isCompleted; // Set processing to true until reset is completed
              state.accountStatusData.dailyRealizedPnl = 0; // Reset daily realized PnL on daily reset
              state.accountStatusData.totalUnrealizedPnl = 0; // Reset unrealized PnL on daily reset
              state.accountStatusData.dailyLoss = 0; // Reset daily loss on daily reset
              if (dailyResetEvent.isCompleted) {
                state.accountStatusData.dailyResetProcessing = false;
              } else {
                state.accountStatusData.dailyResetProcessing = true;
              }
            }

            // Update tradeCloseEvent if present in SSE payload
            if (tradeCloseEvent && 'tradeCloseEvent' in eventData) {
              state.accountStatusData.dailyLoss = tradeCloseEvent.dailyLoss;
              state.accountStatusData.maxLoss = tradeCloseEvent.maxLoss;
              state.accountStatusData.totalRealizedPnl = tradeCloseEvent.realizedPnl;
              state.accountStatusData.totalUnrealizedPnl = tradeCloseEvent.unrealizedPnl;
              state.accountStatusData.tradingDays = tradeCloseEvent.tradingDays;
              state.accountStatusData.consistencyCoeff = tradeCloseEvent.consistencyCoeff;
              state.accountStatusData.consistencyScore = tradeCloseEvent.consistencyScore;
              state.accountStatusData.lastTradingDate = new Date().toISOString();
            }

            console.log('[Closeout Reducer] Account status updated from SSE:', {
              dailyLoss: tradeEvent?.dailyLoss,
              maxLoss: tradeEvent?.maxLoss,
              unrealizedPnl: tradeEvent?.unrealizedPnl,
              profitTargetReached: tradeEvent?.profitTargetReached,
              dailyResetEvent,
              tradeCloseEvent,
            });
          }
          break;

        case SSE_EVENT_TYPES.DAILY_LOSS_REACHED:
          if ('data' in event && event.data) {
            const dailyLossEvent = event as DailyLossReachedEvent;
            state.accountStatusData.dailyLossReached = true;
            state.accountStatusData.dailyLossReachedEvent = dailyLossEvent.data;

            console.log('[Closeout Reducer] Daily loss reached event received:', {
              signalId: dailyLossEvent.data.signalId,
              closingPositions: dailyLossEvent.data.closingPositionsCount,
              triggeredAt: dailyLossEvent.data.triggeredAt,
            });
          }
          break;

        case SSE_EVENT_TYPES.MAX_LOSS_REACHED:
          if ('data' in event && event.data) {
            const maxLossEvent = event as MaxLossReachedEvent;
            state.accountStatusData.dailyLossReached = false;
            if (!state.accountStatusData.totalLossReached) {
              state.accountStatusData.totalLossReached = true;
            }
            state.accountStatusData.maxLossEvent = maxLossEvent.data;

            console.log('[Closeout Reducer] Max loss reached event received:', {
              signalId: maxLossEvent.data.signalId,
              closingPositions: maxLossEvent.data.closingPositionsCount,
              triggeredAt: maxLossEvent.data.triggeredAt,
            });
          }
          break;

        case SSE_EVENT_TYPES.PROFIT_TARGET_REACHED:
          if ('data' in event && event.data) {
            const profitTargetEvent = event as ProfitTargetReachedEvent;
            state.accountStatusData.isTradingPlanCompleted = true;
            state.accountStatusData.profitTargetReachedEvent = profitTargetEvent.data;

            // Set trading plan completed flag when profit target is reached
            if (!state.isTradingPlanCompleted) {
              state.isTradingPlanCompleted = true;
            }

            console.log('[Closeout Reducer] Profit target reached event received:', {
              signalId: profitTargetEvent.data.signalId,
              performanceSnapshot: profitTargetEvent.data.performanceSnapshot,
            });
          }
          break;

        case SSE_EVENT_TYPES.DAILY_AUTO_CLOSE:
          state.isDailyAutoClose = true;
          break;

        default:
          // Log unknown event types
          console.warn('[Closeout Reducer] Unknown SSE event type:', event.type);
      }
    },



  },
});

export const {
  getAccountCloseoutStatus,
  getAccountCloseoutStatusFailed,
  getAccountCloseoutStatusCompleted,
  fetchCloseoutStatusOnLastClose,
  sseConnectionClosed,
  sseConnectionError,
  resetAccountCloseoutState,
  sseEventReceived,
} = closeoutSlice.actions;

export default closeoutSlice.reducer;
